/* eslint-disable max-lines-per-function */
import isEmpty from 'lodash/isEmpty';
import { MutableRefObject } from 'react';

import { VisitPrescription } from '@dreryk/egabinet-visit-frontend-api';

import { dateFormatter, Translate, useTranslate } from '@dreryk/react-common';
import {
   Separator,
   SidePanelListNoItemsMessage,
   StatusBadge,
   StyledFlex,
   StyledMargin,
} from '@dreryk/react-components';

import { VisitDiagnoses } from '~libs.shared.visit';

import { medicinePrescribedForMoreThanMonth } from '../../features/prescriptions';
import { historicalVisitTranslationKeys, reimbursementsTranslationKeys } from '../../translations';

import { HistoricalVisitItemHeaderCopyIcon } from './HistoricalVisitItemHeaderCopyIcon';
import { HistoricalVisitPrescriptionHeader } from './HistoricalVisitPrescriptionHeader';
import { StyledHistoricalVisitPrescription } from './StyledHistoricalVisitPrescription';
import { HistoricalVisitPrescriptionDosage } from './HistoricalVisitPrescriptionDosage';
import { CopyPrescriptionAction } from './actions/CopyPrescriptionAction';
import { useHistoricalVisitPrescriptionsOverlayRefs } from './HistoricalVisitPrescriptions.hooks';

const t = historicalVisitTranslationKeys;

type HistoricalVisitPrescriptionsProps = Readonly<{
   prescriptions: VisitPrescription[];
   copyingAvailable: boolean;
   targetVisitId: number;
   patientId: number;
   visitDiagnoses: VisitDiagnoses;
   backdropElementRef?: MutableRefObject<HTMLElement | null>;
}>;

// eslint-disable-next-line max-lines-per-function
export const HistoricalVisitPrescriptions = (props: HistoricalVisitPrescriptionsProps): JSX.Element => {
   const translate = useTranslate();

   const { setContainerRef, createItemRef, getItemRef } = useHistoricalVisitPrescriptionsOverlayRefs(
      props.backdropElementRef
   );

   if (isEmpty(props.prescriptions)) {
      return <SidePanelListNoItemsMessage message={t.noPrescriptionIssued} />;
   }

   return (
      <div ref={setContainerRef}>
         {props.prescriptions.map((prescription, idx) => {
            const lastElement = idx === props.prescriptions.length - 1;
            const firstElement = idx === 0;

            return (
               <StyledHistoricalVisitPrescription
                  annulled={prescription.annulled}
                  firstElement={firstElement}
                  key={prescription.id}
                  direction={'column'}
               >
                  {prescription.annulled ? (
                     <StyledMargin bottom={10}>
                        <StyledFlex justifyContent={'flex-start'}>
                           <StatusBadge testId={'prescriptionStatus'} type={'unapproved'} compact>
                              <Translate>{t.removed}</Translate>
                           </StatusBadge>
                        </StyledFlex>
                     </StyledMargin>
                  ) : null}

                  <StyledFlex direction={'column'} gap={'10px'}>
                     <StyledFlex justifyContent={'space-between'}>
                        <StyledFlex direction={'column'} ref={createItemRef(prescription.id)}>
                           <StyledFlex direction={'column'} gap={'10px'}>
                              <HistoricalVisitPrescriptionHeader
                                 name={prescription.name}
                                 form={prescription.form}
                                 packageContent={prescription.packageContent}
                                 activeIngredients={prescription.activeIngredients}
                              />
                              <span>
                                 {prescription.quantity} {translate(t.package)}
                                 {translate(reimbursementsTranslationKeys[prescription.reimbursementType])}
                              </span>
                              <HistoricalVisitPrescriptionDosage prescription={prescription} />
                              <div>
                                 {translate(t.prescriptionCompletionDateFrom)}
                                 {dateFormatter(prescription.issuedFrom)}
                              </div>
                              {medicinePrescribedForMoreThanMonth(prescription.issuedFrom, prescription.issuedTo) && (
                                 <div>
                                    {translate(t.prescriptionCompletionDateTo)}
                                    {dateFormatter(prescription.issuedTo)}
                                 </div>
                              )}
                           </StyledFlex>
                        </StyledFlex>

                        <CopyPrescriptionAction
                           prescriptionId={prescription.prescriptionId}
                           targetVisitId={props.targetVisitId}
                           patientId={props.patientId}
                           visitDiagnoses={props.visitDiagnoses}
                        >
                           {({ copyPrescription }) => (
                              <HistoricalVisitItemHeaderCopyIcon
                                 tooltipText={historicalVisitTranslationKeys.copyIcons.medicine}
                                 onClick={copyPrescription}
                                 visible={!prescription.annulled}
                                 backdropElementRef={getItemRef(prescription.id)}
                              />
                           )}
                        </CopyPrescriptionAction>
                     </StyledFlex>

                     {!lastElement && <Separator />}
                  </StyledFlex>
               </StyledHistoricalVisitPrescription>
            );
         })}
      </div>
   );
};
